  import React, { useState, useEffect } from 'react';
  import { useParams, useNavigate } from 'react-router-dom';
  import { useAuth } from '../../hooks/useAuth';
  import { DataSheet, DataSheetItem, CreateDataSheetItemData } from '../../types/dataSheetTypes';
  import dataSheetService from '../../services/dataSheetService';
  import Button from '../../components/common/Button/Button';
  import InputField from '../../components/common/InputField/InputField';
  import Modal from '../../components/common/Modal/Modal';
  import { formatDateDMY } from '../../utils/helpers';
  import styles from './DataSheetDetail.module.scss';
  import { ToastContainer, toast } from 'react-toastify';
  import 'react-toastify/dist/ReactToastify.css';

  const DataSheetDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { auth } = useAuth();
    const navigate = useNavigate();
    const [dataSheet, setDataSheet] = useState<DataSheet | null>(null);
    const [loading, setLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<DataSheetItem | null>(null);
    const [formLoading, setFormLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<DataSheetItem | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: 'value' | 'value1'; direction: 'asc' | 'desc' } | null>(null);

    // Check if user has data sheet management permissions
    const canManageDataSheets = auth.user?.role === 'admin'

    useEffect(() => {
      if (id) {
        loadDataSheet();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadDataSheet = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const dataSheetData = await dataSheetService.getDataSheet(id);
        setDataSheet(dataSheetData);
      } catch (error) {
        toast.error('Failed to load data sheet');
        navigate('/data-sheets');
      } finally {
        setLoading(false);
      }
    };

    const handleDeleteDataSheet = async () => {
      if (!dataSheet) return;
      
      setDeleting(true);
      try {
        await dataSheetService.deleteDataSheet(dataSheet.id);
        toast.success('Data sheet deleted successfully!');
        navigate('/data-sheets');
      } catch (error) {
        toast.error('Failed to delete data sheet');
        console.error('Failed to delete data sheet:', error);
      } finally {
        setDeleting(false);
      }
    };

    const handleAddItem = async (itemData: CreateDataSheetItemData) => {
      if (!dataSheet) return;
      
      setFormLoading(true);
      try {
        const newItem = await dataSheetService.addDataSheetItem(dataSheet.id, itemData);
        setDataSheet(prev => prev ? {
          ...prev,
          items: [...(prev.items || []), newItem]
        } : null);
        setIsAddItemModalOpen(false);
        toast.success('Item added successfully!');
      } catch (error) {
        toast.error('Failed to add item');
        console.error('Failed to add item:', error);
      } finally {
        setFormLoading(false);
      }
    };

    const handleEditItem = async (itemData: CreateDataSheetItemData) => {
      if (!dataSheet || !editingItem) return;
      
      setFormLoading(true);
      try {
        const updatedItem = await dataSheetService.updateDataSheetItem(
          dataSheet.id, 
          editingItem.id, 
          itemData
        );
        
        console.log('Updated item from API:', updatedItem);
        
        // Update the dataSheet state with the new item
        setDataSheet(prev => {
          if (!prev) return null;
          
          const updatedItems = prev.items?.map(item => 
            item.id === editingItem.id ? updatedItem : item
          ) || [];
          
          console.log('Updated items array:', updatedItems);
          
          return {
            ...prev,
            items: updatedItems
          };
        });
        
        setIsEditModalOpen(false);
        setEditingItem(null);
        toast.success('Item updated successfully!');
        
        // Fallback: Reload the data sheet to ensure UI is in sync
        setTimeout(() => {
          loadDataSheet();
        }, 100);
        
      } catch (error) {
        console.error('Error updating item:', error);
        toast.error('Failed to update item');
      } finally {
        setFormLoading(false);
      }
    };

    const handleDeleteItem = async (item: DataSheetItem) => {
      if (!dataSheet) return;
      
      setItemToDelete(item);
      setDeleteModalOpen(true);
    };

    const confirmDeleteItem = async () => {
      if (!dataSheet || !itemToDelete) return;
      
      setDeleting(true);
      try {
        await dataSheetService.deleteDataSheetItem(dataSheet.id, itemToDelete.id);
        setDataSheet(prev => prev ? {
          ...prev,
          items: prev.items?.filter(item => item.id !== itemToDelete.id) || []
        } : null);
        setDeleteModalOpen(false);
        setItemToDelete(null);
        toast.success('Item deleted successfully!');
      } catch (error) {
        toast.error('Failed to delete item');
        console.error('Failed to delete item:', error);
      } finally {
        setDeleting(false);
      }
    };

    const handleSort = (key: 'value' | 'value1') => {
      setSortConfig(prev => {
        if (prev?.key === key) {
          return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
        }
        return { key, direction: 'asc' };
      });
    };

    const getSortedItems = (): DataSheetItem[] => {
      const items = dataSheet?.items || [];
      if (!sortConfig) return items;

      const { key, direction } = sortConfig;
      const sorted = [...items].sort((a, b) => {
        const aValue = a?.[key];
        const bValue = b?.[key];

        const aDate = aValue ? new Date(aValue).getTime() : NaN;
        const bDate = bValue ? new Date(bValue).getTime() : NaN;

        let comparison: number;
        if (!isNaN(aDate) && !isNaN(bDate)) {
          comparison = aDate - bDate;
        } else {
          comparison = (aValue || '').localeCompare(bValue || '');
        }

        return direction === 'asc' ? comparison : -comparison;
      });

      return sorted;
    };

    const getSortIndicator = (key: 'value' | 'value1') => {
      if (sortConfig?.key !== key) return ' ⇅';
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    const downloadCSV = () => {
      if (!dataSheet) return;

      const headers = ['Description', 'Value', 'Value 1'];
      const csvContent = [
        headers.join(','),
        ...(dataSheet.items || []).map(item => [
          item.item_des,
          item.value,
          item.value1
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${dataSheet.sheet_name}_items_${new Date().toLocaleDateString('en-CA')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    if (loading) {
      return (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading data sheet...</p>
        </div>
      );
    }

    if (!dataSheet) {
      return (
        <div className={styles.errorContainer}>
          <h2>Data Sheet Not Found</h2>
          <p>The data sheet you're looking for doesn't exist or has been deleted.</p>
          <Button onClick={() => navigate('/data-sheets')} variant="primary">
            Back to Data Sheets
          </Button>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <Button
              onClick={() => navigate('/data-sheets')}
              variant="secondary"
              size="sm"
              className={styles.backButton}
            >
              ← Back to Data Sheets
            </Button>
            <h2>{dataSheet.sheet_name}</h2>
            <div className={styles.metaInfo}>
              <span>Created: {formatDateDMY(dataSheet.created_at)}</span>
              <span>Updated: {formatDateDMY(dataSheet.updated_at)}</span>
              <span>{dataSheet.items?.length || 0} items</span>
            </div>
          </div>
          
          <div className={styles.actions}>
            <Button
              onClick={downloadCSV}
              variant="secondary"
              size="sm"
            >
              Export CSV
            </Button>
            
            
            
                <Button
                  onClick={() => setIsAddItemModalOpen(true)}
                  variant="primary"
                  size="sm"
                >
                  Add Item
                </Button>
                {/* <Button
                  onClick={() => navigate(`/data-sheets/${dataSheet.id}/edit`)}
                  variant="secondary"
                  size="sm"
                >
                  Edit Sheet
                </Button> */}
                {canManageDataSheets && (
                <Button
                  onClick={handleDeleteDataSheet}
                  variant="danger"
                  size="sm"
                  loading={deleting}
                >
                  Delete Sheet
                </Button>
              
            )}
          </div>
        </div>

        <div className={styles.content}>
          {!dataSheet.items || dataSheet.items.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📝</div>
              <h3>No items in this data sheet</h3>
              <p>Add your first item to get started</p>
              {canManageDataSheets && (
                <Button onClick={() => setIsAddItemModalOpen(true)} variant="primary">
                  Add Item
                </Button>
              )}
            </div>
          ) : (
            <div className={styles.itemsContainer}>
              {/* <div className={styles.itemsHeader}>
                <h2>Items ({dataSheet.items.length})</h2>
              </div> */}
              
              <div className={styles.tableContainer}>
                <table className={styles.itemsTable}>
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Description</th>
                      <th onClick={() => handleSort('value')} style={{ cursor: 'pointer' }}>
                        Value{getSortIndicator('value')}
                      </th>
                      <th onClick={() => handleSort('value1')} style={{ cursor: 'pointer' }}>
                        Value 1{getSortIndicator('value1')}
                      </th>
                      <th>Created</th>
                      {/* <th>Updated</th> */}
                      {canManageDataSheets && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedItems().map((item, index) => {
                      // Skip rendering if item is null/undefined
                      if (!item) return null;
                      
                      return (
                        <tr key={item.id || index}>
                          <td>{index + 1}</td>
                          <td>{item.item_des || '-'}</td>
                          <td>{item.value || '-'}</td>
                          <td>{item.value1 || '-'}</td>
                          <td>{item.created_at ? formatDateDMY(item.created_at) : '-'}</td>
                          {/* <td>{item.updated_at ? formatDateDMY(item.updated_at) : '-'}</td> */}
                          
                            <td>
                              <div className={styles.tableActions}>
                                <Button
                                  onClick={() => {
                                    setEditingItem(item);
                                    setIsEditModalOpen(true);
                                  }}
                                  variant="secondary"
                                  size="sm"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                  </svg>
                                </Button>
                                {canManageDataSheets && (
                                <Button
                                  onClick={() => handleDeleteItem(item)}
                                  variant="danger"
                                  size="sm"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3,6 5,6 21,6"></polyline>
                                    <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                  </svg>
                                </Button>
                                )}
                              </div>
                            </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Add Item Modal */}
        <Modal
          isOpen={isAddItemModalOpen}
          onClose={() => setIsAddItemModalOpen(false)}
          title="Add New Item"
          size="md"
        >
          <ItemForm
            onSubmit={handleAddItem}
            onCancel={() => setIsAddItemModalOpen(false)}
            loading={formLoading}
          />
        </Modal>

        {/* Edit Item Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingItem(null);
          }}
          title="Edit Item"
          size="md"
        >
          <ItemForm
            item={editingItem}
            onSubmit={handleEditItem}
            onCancel={() => {
              setIsEditModalOpen(false);
              setEditingItem(null);
            }}
            loading={formLoading}
          />
        </Modal>

        {/* Delete Item Confirmation Modal */}
        <Modal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title="Confirm Delete"
          size="sm"
        >
          <div className={styles.deleteModal}>
            <p>Are you sure you want to delete this item?</p>
            <p><strong>{itemToDelete?.item_des || 'Unknown Item'}</strong></p>
            <div className={styles.deleteActions}>
              <Button
                onClick={() => setDeleteModalOpen(false)}
                variant="secondary"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteItem}
                variant="danger"
                loading={deleting}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>

        <ToastContainer />
      </div>
    );
  };

  // Item Form Component
  interface ItemFormProps {
    item?: DataSheetItem | null;
    onSubmit: (data: CreateDataSheetItemData) => void;
    onCancel: () => void;
    loading?: boolean;
  }

  const ItemForm: React.FC<ItemFormProps> = ({
    item,
    onSubmit,
    onCancel,
    loading = false
  }) => {
    const [itemDes, setItemDes] = useState('');
    const [value, setValue] = useState('');
    const [value1, setValue1] = useState('');
    const [valueType, setValueType] = useState('text');
    const [valueType1, setValueType1] = useState('text');
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Reset form when item changes or when form is opened/closed
    useEffect(() => {
      if (item) {
        setItemDes(item.item_des || '');
        setValue(item.value || '');
        setValue1(item.value1 || '');
        setValueType(item.valueType || 'text');
        setValueType1(item.valueType1 || 'text');
      } else {
        // Reset form for new item
        setItemDes('');
        setValue('');
        setValue1('');
        setValueType('text');
        setValueType1('text');
      }
      setErrors({});
    }, [item]);

    const validateForm = () => {
      const newErrors: { [key: string]: string } = {};

      if (!itemDes.trim()) {
        newErrors.itemDes = 'Description is required';
      }
      if (!value.trim()) {
        newErrors.value = 'Value is required';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!validateForm()) {
        return;
      }

      const formData = {
        item_des: itemDes.trim(),
        value: value.trim(),
        value1: value1.trim(),
        valueType: valueType.trim(),
        valueType1: valueType1.trim()
      };

      onSubmit(formData);
    };

    return (
      <form onSubmit={handleSubmit} className={styles.itemForm}>
        <InputField
          label="Description"
          name="itemDes"
          type="text"
          value={itemDes}
          onChange={(e) => setItemDes(e.target.value)}
          placeholder="Enter item description"
          error={errors.itemDes}
          required
        />

        {/* Value field with type selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select
            value={valueType}
            onChange={e => setValueType(e.target.value)}
            style={{ height: 45, minWidth: 90, marginRight: 8, marginTop: 18 }}
          >
            <option value="text">Text</option>
            <option value="date">Date</option>
          </select>
          <div style={{ flex: 1 }}>
            <InputField
              label="Value"
              name="value"
              type={valueType === 'date' ? 'date' : 'text'}
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={valueType === 'date' ? 'Select date' : 'Enter item value'}
              error={errors.value}
              required
            />
          </div>
        </div>

        {/* Value 1 field with type selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <select
            value={valueType1}
            onChange={e => setValueType1(e.target.value)}
            style={{ height: 45, minWidth: 90, marginRight: 8, marginTop: 18 }}
          >
            <option value="text">Text</option>
            <option value="date">Date</option>
          </select>
          <div style={{ flex: 1 }}>
            <InputField
              label="Value 1"
              name="value1"
              type={valueType1 === 'date' ? 'date' : 'text'}
              value={value1}
              onChange={e => setValue1(e.target.value)}
              placeholder={valueType1 === 'date' ? 'Select date' : 'Enter item value 1'}
              required
            />
          </div>
        </div>

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {item ? 'Update Item' : 'Add Item'}
          </Button>
        </div>
      </form>
    );
  };

  export default DataSheetDetail; 
